/**
 * FHIR R4 Resource Builders — functions that construct individual FHIR resources
 * from Arete's internal data models.
 */

import { FHIR_SYSTEMS, LOINC_CODES, PHQ4_ITEMS, PHQ4_ANSWERS, LAB_MARKER_TO_LOINC } from './constants';

// ── Types ────────────────────────────────────────────────────────────────────

interface FhirResource {
  resourceType: string;
  id: string;
  // FHIR resources carry heterogeneous dynamic fields (meta, code,
  // subject, valueQuantity, etc.). A precise union is impractical for
  // the 6 resource types we build; the index signature keeps the
  // builders concise while the validator enforces structure at runtime.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [key: string]: any;
}

// ── Patient ──────────────────────────────────────────────────────────────────

/** Build a FHIR R4 Patient resource from an Arete user profile. */
export function buildPatient(profile: {
  id: string;
  full_name?: string | null;
  dob?: string | null;
}): FhirResource {
  const nameParts = (profile.full_name || 'Unknown').split(' ');
  const family = nameParts.length > 1 ? nameParts.slice(1).join(' ') : nameParts[0];
  const given = nameParts.length > 1 ? [nameParts[0]] : [];

  return {
    resourceType: 'Patient',
    id: `patient-${profile.id}`,
    name: [{ family, given, use: 'official' }],
    ...(profile.dob ? { birthDate: profile.dob } : {}),
  };
}

// ── Observation ──────────────────────────────────────────────────────────────

/** Build a FHIR R4 Observation for a wearable or lab data point. */
export function buildObservation(params: {
  id: string;
  patientId: string;
  loincKey: string;
  category: 'vital-signs' | 'activity' | 'laboratory' | 'survey';
  value: number;
  effectiveDateTime?: string;
  effectivePeriod?: { start: string; end: string };
  deviceReference?: string;
  referenceRange?: { low?: number; high?: number; unit: string };
  interpretation?: 'N' | 'H' | 'L';
}): FhirResource | null {
  const loinc = LOINC_CODES[params.loincKey];
  if (!loinc) return null;

  const obs: FhirResource = {
    resourceType: 'Observation',
    id: params.id,
    status: 'final',
    category: [{
      coding: [{
        system: FHIR_SYSTEMS.observationCategory,
        code: params.category,
      }],
    }],
    code: {
      coding: [{
        system: FHIR_SYSTEMS.loinc,
        code: loinc.code,
        display: loinc.display,
      }],
    },
    subject: { reference: `Patient/${params.patientId}` },
    valueQuantity: {
      value: params.value,
      unit: loinc.unit,
      system: FHIR_SYSTEMS.ucum,
      code: loinc.ucum,
    },
  };

  if (params.effectiveDateTime) obs.effectiveDateTime = params.effectiveDateTime;
  if (params.effectivePeriod) obs.effectivePeriod = params.effectivePeriod;
  if (params.deviceReference) obs.device = { reference: params.deviceReference };

  if (params.referenceRange) {
    obs.referenceRange = [{
      ...(params.referenceRange.low != null ? { low: { value: params.referenceRange.low, unit: params.referenceRange.unit, system: FHIR_SYSTEMS.ucum } } : {}),
      ...(params.referenceRange.high != null ? { high: { value: params.referenceRange.high, unit: params.referenceRange.unit, system: FHIR_SYSTEMS.ucum } } : {}),
    }];
  }

  if (params.interpretation) {
    const interpMap: Record<string, { code: string; display: string }> = {
      N: { code: 'N', display: 'Normal' },
      H: { code: 'H', display: 'High' },
      L: { code: 'L', display: 'Low' },
    };
    const interp = interpMap[params.interpretation];
    if (interp) {
      obs.interpretation = [{
        coding: [{ system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationInterpretation', ...interp }],
      }];
    }
  }

  // Tag patient-generated health data for wearables
  if (params.category !== 'laboratory') {
    obs.meta = { tag: [{ system: 'http://terminology.hl7.org/CodeSystem/common-tags', code: 'PGHD', display: 'Patient Generated Health Data' }] };
  }

  return obs;
}

// ── PHQ-4 QuestionnaireResponse ──────────────────────────────────────────────

/** Build a FHIR R4 QuestionnaireResponse for a PHQ-4 entry. */
export function buildPHQ4Response(params: {
  id: string;
  patientId: string;
  authored: string;
  answers: [number, number, number, number]; // q1-q4 values (0-3)
}): FhirResource {
  return {
    resourceType: 'QuestionnaireResponse',
    id: params.id,
    status: 'completed',
    questionnaire: FHIR_SYSTEMS.phq4Questionnaire,
    subject: { reference: `Patient/${params.patientId}` },
    authored: params.authored,
    item: PHQ4_ITEMS.map((item, i) => ({
      linkId: item.linkId,
      text: item.text,
      answer: [{
        valueCoding: {
          system: FHIR_SYSTEMS.phq4AnswerList,
          code: PHQ4_ANSWERS[params.answers[i]]?.code ?? 'unknown',
          display: PHQ4_ANSWERS[params.answers[i]]?.display ?? 'Unknown',
        },
      }],
    })),
  };
}

/** Build a FHIR R4 Observation for a derived PHQ-4 total score. */
export function buildPHQ4ScoreObservation(params: {
  id: string;
  patientId: string;
  score: number;
  effectiveDateTime: string;
  questionnaireResponseId: string;
}): FhirResource {
  return {
    resourceType: 'Observation',
    id: params.id,
    status: 'final',
    category: [{ coding: [{ system: FHIR_SYSTEMS.observationCategory, code: 'survey' }] }],
    code: { coding: [{ system: FHIR_SYSTEMS.loinc, code: '69724-3', display: 'PHQ-4 Total Score' }] },
    subject: { reference: `Patient/${params.patientId}` },
    effectiveDateTime: params.effectiveDateTime,
    valueQuantity: { value: params.score, unit: '{score}', system: FHIR_SYSTEMS.ucum, code: '{score}' },
    derivedFrom: [{ reference: `QuestionnaireResponse/${params.questionnaireResponseId}` }],
  };
}

// ── DiagnosticReport ─────────────────────────────────────────────────────────

/** Build a FHIR R4 DiagnosticReport grouping lab observations from one blood draw. */
export function buildDiagnosticReport(params: {
  id: string;
  patientId: string;
  effectiveDateTime: string;
  observationIds: string[];
}): FhirResource {
  return {
    resourceType: 'DiagnosticReport',
    id: params.id,
    status: 'final',
    category: [{ coding: [{ system: FHIR_SYSTEMS.observationCategory, code: 'laboratory' }] }],
    code: { coding: [{ system: FHIR_SYSTEMS.loinc, code: '24323-8', display: 'Comprehensive metabolic panel' }] },
    subject: { reference: `Patient/${params.patientId}` },
    effectiveDateTime: params.effectiveDateTime,
    result: params.observationIds.map(id => ({ reference: `Observation/${id}` })),
  };
}

// ── Phenotype Observation ────────────────────────────────────────────────────

/** Build a FHIR R4 Observation for an Arete AI phenotype pattern. */
export function buildPhenotypeObservation(params: {
  id: string;
  patientId: string;
  patternId: string;
  patternName: string;
  score: number;
  effectiveDateTime: string;
}): FhirResource {
  return {
    resourceType: 'Observation',
    id: params.id,
    status: 'final',
    category: [{ coding: [{ system: FHIR_SYSTEMS.observationCategory, code: 'survey' }] }],
    code: {
      coding: [{
        system: FHIR_SYSTEMS.areteCodeSystem,
        code: params.patternId,
        display: params.patternName,
      }],
    },
    subject: { reference: `Patient/${params.patientId}` },
    effectiveDateTime: params.effectiveDateTime,
    valueQuantity: { value: params.score, unit: '{score}', system: FHIR_SYSTEMS.ucum, code: '{score}' },
  };
}

/** Look up the LOINC key for an Arete lab marker name. Returns null if not mapped. */
export function labMarkerToLoincKey(marker: string): string | null {
  return LAB_MARKER_TO_LOINC[marker] ?? null;
}
